import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useEditor, EditorContent, Extension, Mark } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { sanitize, textToHtml, htmlToText, normalizeUrl, esc, escAttr, PH_RE } from '../../utils/richText';

/* The Compose Email message box (spec/33), on TipTap. Bold, italic, underline,
   strikethrough | bulleted list, numbered list | link, clear formatting — and
   nothing else, on purpose: no fonts, sizes, colours, alignment, headings or
   inline images, so an email from the class looks like the class's email.
   Shortcuts ⌘/Ctrl + B, I, U, and K for a link. What comes out of it is
   sanitized HTML (p br b i u s a ul ol li) and its plain-text twin. */

/* A template's {{key}} spot. The mark carries the field key, so a field change
   can rewrite its own text and nothing else — the bold or link a room parent put
   around it survives. It renders as <span data-f>, which sanitize() unwraps, so
   it never leaves the editor. */
const TemplateField = Mark.create({
  name: 'templateField',
  inclusive: false,
  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-f'),
        renderHTML: (attrs) => ({ 'data-f': attrs.key }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-f]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },
});

/* Red [brackets] and {{tokens}}: painted as decorations, so nothing is added
   to the draft and the caret, selection and undo history are never disturbed. */
const PlaceholderHighlight = Extension.create({
  name: 'placeholderHighlight',
  addProseMirrorPlugins() {
    const re = new RegExp(PH_RE.source, 'g');
    const build = (doc) => {
      const decos = [];
      doc.descendants((node, pos) => {
        if (!node.isText) return;
        let m;
        re.lastIndex = 0;
        while ((m = re.exec(node.text))) {
          decos.push(Decoration.inline(pos + m.index, pos + m.index + m[0].length, { class: 'ph' }));
        }
      });
      return DecorationSet.create(doc, decos);
    };
    return [
      new Plugin({
        key: new PluginKey('placeholderHighlight'),
        state: {
          init: (_, state) => build(state.doc),
          apply: (tr, old) => (tr.docChanged ? build(tr.doc) : old),
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const MOD = MAC ? '⌘' : 'Ctrl+';

const Ic = {
  ul: <svg viewBox="0 0 24 24"><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r=".9" /><circle cx="4.5" cy="12" r=".9" /><circle cx="4.5" cy="18" r=".9" /></svg>,
  ol: <svg viewBox="0 0 24 24"><path d="M10 6h10M10 12h10M10 18h10" /><path d="M4 5l1.4-.9V9M3.6 14.6c.3-.9 2.4-.9 2.4.3 0 1-2.4 1.6-2.4 2.7h2.5" strokeWidth="1.5" /></svg>,
  link: <svg viewBox="0 0 24 24"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></svg>,
  clear: <svg viewBox="0 0 24 24"><path d="M5 5h10M10 5l-2 14M6 19h6" /><path d="M15 13l5 5M20 13l-5 5" /></svg>,
};

const TOOLS = [
  { id: 'bold', label: 'Bold', key: 'B', mark: 'bold', body: <b>B</b> },
  { id: 'italic', label: 'Italic', key: 'I', mark: 'italic', body: <i>I</i> },
  { id: 'underline', label: 'Underline', key: 'U', mark: 'underline', body: <u>U</u> },
  { id: 'strike', label: 'Strikethrough', mark: 'strike', body: <s>S</s> },
  { sep: true },
  { id: 'ul', label: 'Bulleted list', mark: 'bulletList', body: Ic.ul },
  { id: 'ol', label: 'Numbered list', mark: 'orderedList', body: Ic.ol },
  { sep: true },
  { id: 'link', label: 'Link', key: 'K', body: Ic.link },
  { id: 'clear', label: 'Clear formatting', body: Ic.clear },
];

const RichEditor = forwardRef(function RichEditor({ initialHtml = '', placeholder = '', ariaLabel = 'Message', onChange }, ref) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editorRef = useRef(null);
  const openLinkRef = useRef(() => {});

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [linkErr, setLinkErr] = useState('');
  const [linkExists, setLinkExists] = useState(false);
  const linkInputRef = useRef(null);

  const emit = useCallback((ed) => {
    const html = sanitize(ed.getHTML());
    onChangeRef.current?.({ html, text: htmlToText(html), empty: ed.isEmpty });
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        dropcursor: false,
        gapcursor: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        validate: (href) => /^(https?:|mailto:|tel:)/i.test(href),
      }),
      TemplateField,
      PlaceholderHighlight,
    ],
    content: initialHtml ? sanitize(initialHtml) : '',
    editorProps: {
      attributes: {
        class: 'rte-area',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel,
        spellcheck: 'true',
      },
      /* a paste from Word or Google Docs is reduced to the same eight tags */
      transformPastedHTML: (html) => sanitize(html),
      handleKeyDown: (view, event) => {
        if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          openLinkRef.current();
          return true;
        }
        return false;
      },
      /* files go in Attachments, not in the message */
      handleDrop: (view, event) => {
        if (event.dataTransfer?.files?.length) {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => emit(ed),
  });
  editorRef.current = editor;

  /* ── links ── */
  const openLink = useCallback(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const href = ed.getAttributes('link')?.href || '';
    setLinkValue(href);
    setLinkExists(!!href);
    setLinkErr('');
    setLinkOpen(true);
    setTimeout(() => {
      linkInputRef.current?.focus();
      linkInputRef.current?.select();
    }, 0);
  }, []);
  openLinkRef.current = openLink;

  const closeLink = (refocus) => {
    setLinkOpen(false);
    setLinkErr('');
    if (refocus) editorRef.current?.commands.focus();
  };

  const applyLink = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const url = normalizeUrl(linkValue);
    if (!url) {
      setLinkErr('Enter a web address, like https://roomparent.com, or an email address.');
      linkInputRef.current?.focus();
      return;
    }
    const inLink = !!ed.getAttributes('link')?.href;
    if (inLink || !ed.state.selection.empty) {
      ed.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      /* nothing selected: the address itself is the link text */
      ed.chain().focus().insertContent(`<a href="${escAttr(url)}">${esc(linkValue.trim())}</a> `).run();
    }
    closeLink(false);
  };

  const removeLink = () => {
    editorRef.current?.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLink(false);
  };

  /* ── toolbar ── */
  const run = (id) => {
    const ed = editorRef.current;
    if (!ed) return;
    if (id === 'link') return openLink();
    const c = ed.chain().focus();
    if (id === 'bold') c.toggleBold();
    else if (id === 'italic') c.toggleItalic();
    else if (id === 'underline') c.toggleUnderline();
    else if (id === 'strike') c.toggleStrike();
    else if (id === 'ul') c.toggleBulletList();
    else if (id === 'ol') c.toggleOrderedList();
    /* clear = the formatting marks and the link; a template's field marks stay,
       they are the editor's bookkeeping, not formatting */
    else if (id === 'clear') c.unsetBold().unsetItalic().unsetUnderline().unsetStrike().unsetLink();
    c.run();
    return undefined;
  };

  /* ── what the page drives through the ref ── */
  useImperativeHandle(
    ref,
    () => ({
      /* trusted: the caller built this HTML itself (a template's field spans), so it is not sanitized */
      setContent(html, trusted = false) {
        const ed = editorRef.current;
        if (!ed) return;
        ed.commands.setContent(html ? (trusted ? html : sanitize(html)) : '', false);
        emit(ed);
      },
      setText(text) {
        this.setContent(textToHtml(text), true);
      },
      /* rewrite the text of one template field's spans and nothing else */
      setFieldValue(key, text) {
        const ed = editorRef.current;
        if (!ed) return;
        const type = ed.schema.marks.templateField;
        const ranges = [];
        ed.state.doc.descendants((node, pos) => {
          if (!node.isText) return;
          if (!node.marks.some((m) => m.type === type && m.attrs.key === key)) return;
          const last = ranges[ranges.length - 1];
          if (last && last.to === pos) last.to = pos + node.nodeSize;
          else ranges.push({ from: pos, to: pos + node.nodeSize, marks: node.marks });
        });
        if (!ranges.length) return;
        let tr = ed.state.tr;
        [...ranges].reverse().forEach((r) => {
          tr = text ? tr.replaceWith(r.from, r.to, ed.schema.text(text, r.marks)) : tr.delete(r.from, r.to);
        });
        ed.view.dispatch(tr);
      },
      getHTML: () => (editorRef.current ? sanitize(editorRef.current.getHTML()) : ''),
      getText: () => (editorRef.current ? htmlToText(sanitize(editorRef.current.getHTML())) : ''),
      isEmpty: () => (editorRef.current ? editorRef.current.isEmpty : true),
      focus: () => editorRef.current?.commands.focus(),
    }),
    [emit]
  );

  useEffect(() => () => editorRef.current?.destroy?.(), []);

  const active = (t) => (t.mark && editor ? editor.isActive(t.mark) : false);

  return (
    <div className="rte">
      <div className="rte-bar" role="toolbar" aria-label="Text formatting">
        {TOOLS.map((t, i) =>
          t.sep ? (
            <span className="rte-sep" aria-hidden="true" key={`sep-${i}`} />
          ) : (
            <button
              type="button"
              className="rte-btn"
              key={t.id}
              aria-label={t.label}
              title={`${t.label}${t.key ? ` (${MOD}${t.key})` : ''}`}
              aria-pressed={t.mark ? active(t) : undefined}
              onMouseDown={(e) => e.preventDefault() /* keep the selection */}
              onClick={() => run(t.id)}
            >
              {t.body}
            </button>
          )
        )}
      </div>
      {linkOpen && (
        <div className="rte-link">
          <label>
            Link address
            <input
              ref={linkInputRef}
              type="text"
              inputMode="url"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="https://"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  closeLink(true);
                }
              }}
            />
          </label>
          <button type="button" className="rte-go" onClick={applyLink}>Apply</button>
          {linkExists && (
            <button type="button" className="rte-rm" onClick={removeLink}>Remove link</button>
          )}
          <button type="button" className="rte-x" aria-label="Cancel" onClick={() => closeLink(true)}>Cancel</button>
          <div className="rte-err" role="alert">{linkErr}</div>
        </div>
      )}
      <div className="rte-wrap">
        {editor?.isEmpty && placeholder ? <div className="rte-ph" aria-hidden="true">{placeholder}</div> : null}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
});

export default RichEditor;
